import React from 'react';
import { Link } from 'react-router-dom';
import QRCodeImage from './QRCodeImage.jsx';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888', surface: '#111111' };

const REDIRECT_BASE_URL = typeof import.meta !== 'undefined' && import.meta.env?.VITE_REDIRECT_BASE_URL
  ? import.meta.env.VITE_REDIRECT_BASE_URL
  : 'https://r.qrqy.shop';

/**
 * QRCard — displays a single owned QR code with summary stats.
 *
 * @param {object} props
 * @param {string}      props.code          - QR code identifier (e.g. "ABC123")
 * @param {string}      props.destination   - Current redirect destination URL
 * @param {number}      props.scanCount     - Total lifetime scan count
 * @param {string|null} props.lastScanned   - ISO date string of last scan, or null
 * @param {string}      props.productName   - Human-readable product name (e.g. "QR TEE")
 * @param {boolean}     [props.isActive]    - Whether this code is active
 */
export default function QRCard({
  code,
  destination,
  scanCount = 0,
  lastScanned = null,
  productName = 'QRQY PIECE',
  isActive = true,
}) {
  // Truncate destination URL for display
  const displayDestination = destination
    ? destination.replace(/^https?:\/\//, '').substring(0, 38) + (destination.length > 44 ? '...' : '')
    : 'No destination set';

  const lastScannedDisplay = lastScanned
    ? new Date(lastScanned).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Never';

  return (
    <article style={{
      backgroundColor: COLORS.surface,
      border: `1px solid rgba(255,255,255,0.08)`,
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
      position: 'relative',
    }}>
      {/* Active/inactive badge */}
      <div style={{
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        fontSize: '0.6rem',
        letterSpacing: '0.15em',
        fontWeight: 700,
        color: isActive ? COLORS.gold : COLORS.gray,
        border: `1px solid ${isActive ? COLORS.gold : 'rgba(255,255,255,0.15)'}`,
        padding: '0.2rem 0.5rem',
      }}>
        {isActive ? 'ACTIVE' : 'INACTIVE'}
      </div>

      {/* Real QR code thumbnail */}
      <QRCodeImage
        value={destination ? `${REDIRECT_BASE_URL}/r/${code}` : ''}
        size={80}
      />

      {/* Code ID */}
      <div>
        <p style={{
          fontSize: '0.6rem',
          letterSpacing: '0.2em',
          color: COLORS.gray,
          margin: '0 0 0.25rem',
          fontWeight: 600,
        }}>
          {productName}
        </p>
        <p style={{
          fontSize: '1rem',
          fontWeight: 800,
          letterSpacing: '0.15em',
          color: COLORS.white,
          margin: 0,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {code}
        </p>
      </div>

      {/* Destination */}
      <div>
        <p style={{
          fontSize: '0.6rem',
          letterSpacing: '0.15em',
          color: COLORS.gray,
          margin: '0 0 0.25rem',
          fontWeight: 600,
        }}>
          REDIRECTS TO
        </p>
        <p style={{
          fontSize: '0.78rem',
          color: destination ? COLORS.white : COLORS.gray,
          margin: 0,
          fontFamily: destination ? 'monospace' : 'inherit',
          opacity: destination ? 0.9 : 0.5,
          wordBreak: 'break-all',
        }}>
          {displayDestination}
        </p>
      </div>

      {/* Stats row */}
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        paddingTop: '0.75rem',
        borderTop: `1px solid rgba(255,255,255,0.06)`,
      }}>
        <div>
          <p style={{
            fontSize: '0.6rem',
            letterSpacing: '0.12em',
            color: COLORS.gray,
            margin: '0 0 0.2rem',
          }}>
            SCANS
          </p>
          <p style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: scanCount > 0 ? COLORS.gold : COLORS.white,
            margin: 0,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {scanCount.toLocaleString()}
          </p>
        </div>
        <div>
          <p style={{
            fontSize: '0.6rem',
            letterSpacing: '0.12em',
            color: COLORS.gray,
            margin: '0 0 0.2rem',
          }}>
            LAST SCAN
          </p>
          <p style={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: COLORS.white,
            margin: 0,
            opacity: lastScanned ? 0.85 : 0.35,
          }}>
            {lastScannedDisplay}
          </p>
        </div>
      </div>

      {/* Edit link */}
      <Link
        to={`/qr/${code}`}
        style={{
          display: 'block',
          textAlign: 'center',
          padding: '0.7rem',
          border: `1px solid rgba(255,255,255,0.15)`,
          color: COLORS.white,
          textDecoration: 'none',
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.18em',
          transition: 'border-color 0.15s, color 0.15s',
          marginTop: 'auto',
        }}
      >
        EDIT
      </Link>
    </article>
  );
}
